import { Box, Button, Group, Stack, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconClock, IconMailCheck } from '@tabler/icons-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';
import { RegistrationLayout } from './RegistrationLayout';

interface SentSearch {
  email?: string;
}

/**
 * Interstitial after the user submits the Start form. Shows the email the link was sent to and a
 * 24-hour-expiry hint.
 *
 * **This is the end of the road until the email arrives**, and that is the point. There used to be
 * a button here that jumped straight to step 3, using a verification token the API returned in the
 * 202's Location header. It was labelled "(dev)" but it was never only dev: the token came back in
 * every environment, so anyone who could call the endpoint could finish a registration without
 * access to the mailbox, and a tenant could be created under somebody else's address. The token no
 * longer leaves the server, and the emailed link is the only way on.
 */
export function SentRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as SentSearch;

  return (
    <RegistrationLayout step="step2Of3">
      <Stack align="center" gap="md" py="md">
        <Box
          w={56}
          h={56}
          style={{
            borderRadius: '50%',
            background: 'var(--mantine-color-invetrak-1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconMailCheck size={28} color="var(--mantine-color-invetrak-7)" />
        </Box>
        <Stack gap={4} align="center">
          <Title order={2} ta="center">{t('registration.sent.title')}</Title>
          <Text size="sm" c="dimmed" ta="center">{t('registration.sent.weSentTo')}</Text>
          <Text size="sm" fw={500}>{search.email ?? ''}</Text>
        </Stack>

        <Group
          gap="sm"
          align="flex-start"
          p="sm"
          w="100%"
          style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 8 }}
        >
          <IconClock size={18} color="var(--mantine-color-gray-7)" />
          <Text size="sm" c="dimmed" style={{ flex: 1 }}>
            <Trans
              i18nKey="registration.sent.expiresIn"
              components={{ strong: <Text component="span" c="dark" fw={500} /> }}
            />{' '}
            <Text component="a" href="#" c="invetrak.7" size="sm" td="underline">
              {t('registration.sent.resend')}
            </Text>.
          </Text>
        </Group>

        <Button
          variant="default"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => void navigate({ to: '/register' })}
        >
          {t('registration.sent.useDifferentEmail')}
        </Button>
      </Stack>
    </RegistrationLayout>
  );
}
